/**
 * Pure state derivation for the KG pipeline trigger flow.
 *
 * All status interpretation lives here so the hook and the UI never
 * branch on raw API strings. Unknown statuses from the backend are
 * treated as still-running so polling continues instead of stalling.
 */

import type { PipelineRunListItem, PipelineRunStatus } from "./pipeline.types";

export type KgPipelinePhase =
  | "idle"
  | "triggering"
  | "polling"
  | "completed"
  | "failed";

/** Shown when a build failed without saying why. */
export const PIPELINE_RUN_FAILED_FALLBACK = "Pipeline run failed";

export const isTerminalPipelineStatus = (
  status: string | null | undefined,
): boolean => status === "completed" || status === "failed";

export interface DerivePipelinePhaseInput {
  /** Trigger mutation in flight. */
  isTriggering: boolean;
  /** Trigger mutation rejected with this message. */
  triggerErrorMessage?: string;
  /** Run id returned by the trigger API; null until a run starts. */
  runId: string | null;
  /** Latest status from the runs API (may lag behind runId). */
  runStatus?: PipelineRunStatus | string;
  /** error_message from the runs API on failure. */
  runErrorMessage?: string | null;
  /** Set once status polling has been abandoned (consecutive fetch failures). */
  pollErrorMessage?: string;
}

export interface PipelinePhaseResult {
  phase: KgPipelinePhase;
  errorMessage?: string;
}

export const derivePipelinePhase = ({
  isTriggering,
  triggerErrorMessage,
  runId,
  runStatus,
  runErrorMessage,
  pollErrorMessage,
}: DerivePipelinePhaseInput): PipelinePhaseResult => {
  if (isTriggering) {
    return { phase: "triggering" };
  }
  if (triggerErrorMessage) {
    return { phase: "failed", errorMessage: triggerErrorMessage };
  }
  if (!runId) {
    return { phase: "idle" };
  }
  if (runStatus === "completed") {
    return { phase: "completed" };
  }
  if (runStatus === "failed") {
    return {
      phase: "failed",
      errorMessage: runErrorMessage ?? PIPELINE_RUN_FAILED_FALLBACK,
    };
  }
  if (pollErrorMessage) {
    return { phase: "failed", errorMessage: pollErrorMessage };
  }
  // pending / running / not-yet-fetched / unknown future status → keep polling
  return { phase: "polling" };
};

/**
 * The newest run of a workspace → what its status pill shows. No run yet → idle;
 * an unknown future status counts as still running, as in `derivePipelinePhase`.
 */
export const deriveLatestRunPhase = (
  run: PipelineRunListItem | null | undefined,
): PipelinePhaseResult => {
  if (!run) {
    return { phase: "idle" };
  }
  if (run.status === "completed") {
    return { phase: "completed" };
  }
  if (run.status === "failed") {
    return {
      phase: "failed",
      errorMessage: run.error_message ?? PIPELINE_RUN_FAILED_FALLBACK,
    };
  }
  return { phase: "polling" };
};

/**
 * Identifies a finished run, so a newly finished one can be told apart from the one
 * already on screen; null while the newest run is still going (or there is none).
 */
export const finishedRunKey = (
  run: PipelineRunListItem | null | undefined,
): string | null =>
  run && isTerminalPipelineStatus(run.status)
    ? `${run.id}:${run.status}:${run.completed_at ?? ""}`
    : null;
